import React, { useContext, useState } from 'react';
import get from 'lodash/get';
import styled from 'styled-components';

import Checkbox from 'src/presentation/components/forms/Checkbox';
import FormContext from 'src/presentation/contexts/FormContext';
import Input from 'src/presentation/components/forms/Input';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

import ChatQuickActionsSection from './ChatQuickActionsSection';
import ChatRulesSection from 'src/presentation/components/pages/chat/sections/ChatRulesSection';
import GoogleAnalyticsSection from './GoogleAnalyticsSection';
import RadioGroup from 'src/presentation/components/forms/RadioGroup';

import { getSelectorOptions } from 'src/presentation/components/pages/shared/utils/Utils';
import { Section } from 'src/presentation/components/forms/Layout';
import {
  ColumnRow,
  CustomRow,
  FixedWidthContentRow,
  HalfRow,
  MediumHeader,
  PrimaryDescription,
  PrimaryHeader,
  StyledImage,
  StyledRow,
} from 'src/presentation/components/pages/chat/ChatLayout';
import {
  AUTOMATED_MESSAGING_TARGET_TYPE_AGENT,
  AUTOMATED_MESSAGING_TARGET_TYPE_AUTOMATION,
  CHAT_ICON_TYPE_OPTIONS,
  CHAT_WIDGET_TYPE,
  CLOSE_BUTTON_TYPE_OPTIONS,
  FIELDS_MAPPER,
  ICON_TEXT,
  SDK_CONFIG_DESCRIPTION,
  SELF_SERVICE_WIDGET_TYPE,
  WORKING_HOURS_SECTION_DESCRIPTION,
} from '../chatConstants';

import CloseButton from 'src/images/CloseButton.svg';
import GreetingCloseButton from 'src/images/GreetingCloseButton.svg';
import MinimizeChatButton from 'src/images/MinimizeChatButton.svg';

import set from 'lodash/set';
import { InputSize } from 'src/presentation/components/forms/InputSize';
import TimeZoneSelector from 'src/presentation/components/pages/shared/TimeZoneSelector';
import WorkingHours from 'src/presentation/components/pages/shared/workingHours/WorkingHours';
import ActivateMessageAutomationModal from 'src/presentation/components/common/ActivateMessageAutomationModal';
import { CHAT_BRAND } from 'src/constants/branding';

const ChatConfigurationSection = ({ errors }) => {
  const { workingState, updateState } = useContext(FormContext);
  const [showMsgAutomationConfirmationModal, setShowMsgAutomationConfirmationModal] = useState<boolean>(false);
  const isFeatureEnabled = useIsFeatureEnabled();
  const address = get(workingState, 'address');
  const sectionHeaderLabel = `Configure ${address}`;
  const automationMessagingTargetType = get(workingState, 'messageAutomationConfiguration.targetType');
  const automationMessagingEnabled = automationMessagingTargetType === AUTOMATED_MESSAGING_TARGET_TYPE_AUTOMATION;
  const widgetType = get(workingState, 'configuration.widgetType');
  const selfServiceEnabled = widgetType === SELF_SERVICE_WIDGET_TYPE;
  const shouldShowAutomationServices = !isFeatureEnabled('enableChannelManagement');

  const toggleSelfServiceWidgetType = ({ checked }) => {
    let changes;
    if (checked) {
      changes = set({}, FIELDS_MAPPER.WIDGET_TYPE.path, SELF_SERVICE_WIDGET_TYPE);
      updateState(changes, { replaceArrayValues: true });
    } else {
      changes = set({}, FIELDS_MAPPER.WIDGET_TYPE.path, CHAT_WIDGET_TYPE);
      updateState(changes, { replaceArrayValues: true });
    }
  };

  const toggleAutomatedMessagingTargetType = ({ checked }) => {
    let changes;
    if (checked) {
      setShowMsgAutomationConfirmationModal(true);
    } else {
      changes = set({}, FIELDS_MAPPER.MESSAGING_AUTOMATION_TARGET_TYPE.path, AUTOMATED_MESSAGING_TARGET_TYPE_AGENT);
      updateState(changes, { replaceArrayValues: true });
    }
  };

  const onProceedWithMessageAutomation = () => {
    const changes = set(
      {},
      FIELDS_MAPPER.MESSAGING_AUTOMATION_TARGET_TYPE.path,
      AUTOMATED_MESSAGING_TARGET_TYPE_AUTOMATION
    );
    updateState(changes, { replaceArrayValues: true });
    setShowMsgAutomationConfirmationModal(false);
  };

  return (
    <>
      {showMsgAutomationConfirmationModal ? (
        <ActivateMessageAutomationModal
          onCancel={() => setShowMsgAutomationConfirmationModal(false)}
          onSubmit={onProceedWithMessageAutomation}
          title={`Enable Automation Services for ${CHAT_BRAND.name}`}
        />
      ) : null}
      <PrimaryHeader>{sectionHeaderLabel}</PrimaryHeader>
      <PrimaryDescription>{SDK_CONFIG_DESCRIPTION}</PrimaryDescription>
      <MediumHeader>Self-Service</MediumHeader>
      {renderWidgetOptions({
        automationMessagingEnabled,
        selfServiceEnabled,
        toggleSelfServiceWidgetType,
        toggleAutomatedMessagingTargetType,
        shouldShowAutomationServices,
      })}
      <ChatRulesSection errors={errors} />
      <Section header="Office Hours" description={WORKING_HOURS_SECTION_DESCRIPTION}>
        <TimeZoneSelector attr={FIELDS_MAPPER.OFFICE_HOURS_TIMEZONE.path} label="Timezone" />
        <WorkingHours attr={FIELDS_MAPPER.OFFICE_HOURS.path} dataAid="officeHours" />
      </Section>
      <ChatQuickActionsSection configuration={workingState} />

      <GoogleAnalyticsSection />

      {renderIconsBlock(workingState)}
    </>
  );
};

export const MobileChatConfigurationSection = () => {
  const { workingState, updateState } = useContext(FormContext);
  const [showMsgAutomationConfirmationModal, setShowMsgAutomationConfirmationModal] = useState<boolean>(false);
  const isFeatureEnabled = useIsFeatureEnabled();
  const address = get(workingState, 'address');
  const sectionHeaderLabel = `Configure ${address}`;
  const automationMessagingTargetType = get(workingState, 'messageAutomationConfiguration.targetType');
  const automationMessagingEnabled = automationMessagingTargetType === AUTOMATED_MESSAGING_TARGET_TYPE_AUTOMATION;
  const shouldShowAutomationServices = !isFeatureEnabled('enableChannelManagement');

  const toggleAutomatedMessagingTargetType = ({ checked }) => {
    let changes;
    if (checked) {
      setShowMsgAutomationConfirmationModal(true);
    } else {
      changes = set({}, FIELDS_MAPPER.MESSAGING_AUTOMATION_TARGET_TYPE.path, AUTOMATED_MESSAGING_TARGET_TYPE_AGENT);
      updateState(changes, { replaceArrayValues: true });
    }
  };

  const onProceedWithMessageAutomation = () => {
    const changes = set(
      {},
      FIELDS_MAPPER.MESSAGING_AUTOMATION_TARGET_TYPE.path,
      AUTOMATED_MESSAGING_TARGET_TYPE_AUTOMATION
    );
    updateState(changes, { replaceArrayValues: true });
    setShowMsgAutomationConfirmationModal(false);
  };

  return (
    <>
      {showMsgAutomationConfirmationModal ? (
        <ActivateMessageAutomationModal
          onCancel={() => setShowMsgAutomationConfirmationModal(false)}
          onSubmit={onProceedWithMessageAutomation}
          title={`Enable Automation Services for ${CHAT_BRAND.name}`}
        />
      ) : null}
      <PrimaryHeader>{sectionHeaderLabel}</PrimaryHeader>
      <PrimaryDescription>
        Configure the Self-Service setting for 3rd Party Automation Services and the Office Hours for your{' '}
        {CHAT_BRAND.name}
      </PrimaryDescription>
      <MediumHeader>Self-Service</MediumHeader>
      {shouldShowAutomationServices && (
        <HalfRow>
          <CheckboxContainer>
            <Checkbox
              checked={!!automationMessagingEnabled}
              label={<CheckboxLabel label={`Enable Automation Services for ${CHAT_BRAND.name}`} />}
              attr={FIELDS_MAPPER.MESSAGING_AUTOMATION_TARGET_TYPE.path}
              data-aid={FIELDS_MAPPER.MESSAGING_AUTOMATION_TARGET_TYPE.path}
              onChange={() => toggleAutomatedMessagingTargetType({ checked: !automationMessagingEnabled })}
              value={automationMessagingEnabled ? 'checked' : ''}
            />
            <CheckboxInfo>{FIELDS_MAPPER.MESSAGING_AUTOMATION_TARGET_TYPE.description}</CheckboxInfo>
          </CheckboxContainer>
        </HalfRow>
      )}
      <Section
        header={`${CHAT_BRAND.name} Hours`}
        description={`Schedule when the ${CHAT_BRAND.name} will be live on your site by setting the timezone and time range.`}
      >
        <TimeZoneSelector attr={FIELDS_MAPPER.OFFICE_HOURS_TIMEZONE.path} label="Timezone" />
        <WorkingHours attr={FIELDS_MAPPER.OFFICE_HOURS.path} dataAid="officeHours" />
      </Section>
    </>
  );
};

function renderWidgetOptions({
  automationMessagingEnabled,
  selfServiceEnabled,
  toggleSelfServiceWidgetType,
  toggleAutomatedMessagingTargetType,
  shouldShowAutomationServices,
}) {
  return (
    <HalfRow>
      <CheckboxContainer>
        {shouldShowAutomationServices && (
          <div>
            <Checkbox
              checked={!!automationMessagingEnabled}
              label={<CheckboxLabel label={`Enable Automation Services for ${CHAT_BRAND.name}`} />}
              attr={FIELDS_MAPPER.MESSAGING_AUTOMATION_TARGET_TYPE.path}
              data-aid={FIELDS_MAPPER.MESSAGING_AUTOMATION_TARGET_TYPE.path}
              onChange={() => toggleAutomatedMessagingTargetType({ checked: !automationMessagingEnabled })}
              value={automationMessagingEnabled ? 'checked' : ''}
            />
            <PaddedCheckboxInfo>{FIELDS_MAPPER.MESSAGING_AUTOMATION_TARGET_TYPE.description}</PaddedCheckboxInfo>
          </div>
        )}
        <Checkbox
          checked={!!selfServiceEnabled}
          label={<CheckboxLabel label="Enable Search and Quick Actions" />}
          attr={FIELDS_MAPPER.WIDGET_TYPE.path}
          data-aid={FIELDS_MAPPER.WIDGET_TYPE.path}
          onChange={() => toggleSelfServiceWidgetType({ checked: !selfServiceEnabled })}
          value={selfServiceEnabled ? 'checked' : ''}
        />
        <CheckboxInfo>{FIELDS_MAPPER.WIDGET_TYPE.description}</CheckboxInfo>
      </CheckboxContainer>
    </HalfRow>
  );
}

const CheckboxLabel = ({ label }) => {
  return <CheckboxLabelText>{label}</CheckboxLabelText>;
};

function renderIconsBlock(configuration) {
  const minimizedChatButtonTypeValue = get(configuration, FIELDS_MAPPER.INITIAL_CHAT_BUTTON_ICON_TYPE.path);
  const minimizedChatButtonSelectOptions = getSelectorOptions(CHAT_ICON_TYPE_OPTIONS);
  const closeButtonSelectOptions = getSelectorOptions(CLOSE_BUTTON_TYPE_OPTIONS);

  const buttonNameInput =
    minimizedChatButtonTypeValue !== ICON_TEXT ? null : (
      <Input
        data-aid={FIELDS_MAPPER.INITIAL_CHAT_BUTTON_TEXT.path}
        attr={FIELDS_MAPPER.INITIAL_CHAT_BUTTON_TEXT.path}
        label="Button name"
      />
    );

  return (
    <Section data-aid="icons">
      <MediumHeader>Buttons</MediumHeader>
      <FixedWidthContentRow>
        <ColumnRow>
          <RadioGroup
            attr={FIELDS_MAPPER.INITIAL_CHAT_BUTTON_ICON_TYPE.path}
            data-aid={FIELDS_MAPPER.INITIAL_CHAT_BUTTON_ICON_TYPE.path}
            label="Minimized chat button"
            options={minimizedChatButtonSelectOptions}
          />
          {buttonNameInput}
        </ColumnRow>
        <ColumnRow>
          <StyledImage src={MinimizeChatButton} />
        </ColumnRow>
      </FixedWidthContentRow>
      <FixedWidthContentRow>
        <CustomRow>
          <StyledRow>
            <RadioGroup
              attr={FIELDS_MAPPER.CLOSE_BUTTON_TYPE.path}
              data-aid={FIELDS_MAPPER.CLOSE_BUTTON_TYPE.path}
              description={FIELDS_MAPPER.CLOSE_BUTTON_TYPE.description}
              label="Close Button"
              options={closeButtonSelectOptions}
            />
          </StyledRow>
        </CustomRow>
        <ColumnRow>
          <StyledImage src={CloseButton} />
          <StyledImage src={GreetingCloseButton} />
        </ColumnRow>
      </FixedWidthContentRow>
    </Section>
  );
}

const CheckboxContainer = styled.span`
  width: ${InputSize.XLARGE};
`;
const CheckboxLabelText = styled.span`
  font-weight: bold;
  display: flex;
`;
const CheckboxInfo = styled.span`
  display: flex;
  line-height: 1.6;
  padding-left: 24px;
  position: relative;
  top: 5px;
  color: ${p => p.theme.colors.gray700};
`;

const PaddedCheckboxInfo = styled(CheckboxInfo)`
  padding-bottom: 20px;
`;

export default ChatConfigurationSection;
